import React, { useMemo, useState } from 'react';
import { memberInContextAtom } from '@src/global-state';
import { useAtomValue } from 'jotai';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { MemberInformation } from '@src/common/MemberInformation/MemberInformation';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Button } from '@uhg-abyss/web/ui/Button';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import {
  Breadcumbs,
  ScreenConstants,
} from '@src/common/BreadCumbs/Breadcrumbs';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { BenefitDetailsResults } from './BenefitDetailsResults';
import { getFeatureFlagsFromStorage } from '@src/utills/storage';

export type Vendor = 'findhelp' | 'Unite Us';

export const BenefitDetails = () => {
  const { getLocation } = useRouter();
  const location = getLocation();
  const memberInContext = useAtomValue(memberInContextAtom);
  const [vendor, _setVendor] = useState<Vendor | null>(null);
  const { navigate } = useRouter();

  const benefitId = location
    ? location.pathname.split('/')[2] + location.hash
    : '';

  const form = useForm({
    defaultValues: {},
  });

  const onSubmit = (data) => {
    if (!data.textForm?.trim()) return;
    navigate(`/benefitsearchpage?benefitsearchterm=${data.textForm}`);
  };

  const featureFlagsFromStorage = getFeatureFlagsFromStorage();

  const isUniteUsApiEnabled: boolean = useMemo(() => {
    const flag: any = featureFlagsFromStorage['agent_uniteus_api'];

    if (!flag) {
      console.error(
        'Unite Us API feature flag did not conform to expected shape'
      );
      return false;
    }

    return flag['enabled'] ?? false;
  }, [featureFlagsFromStorage]);

  return (
    <React.Fragment>
      <div>
        <div style={{ backgroundColor: '#F7FCFD' }} />
        <div
          style={{
            backgroundColor: '#EDF8FA',
            paddingRight: '5rem',
            paddingLeft: '5rem',
            paddingTop: '3rem',
            paddingBottom: '2rem',
          }}
        >
          <Grid>
            <Grid.Col span={{ xs: 12, md: 8, lg: 8 }}>
              <div>
                {sessionStorage.getItem('fromSearch') === 'true' ? (
                  <Breadcumbs screen={ScreenConstants.BENEFIT_DETAILS_WITH_SEARCH} />
                ) : (
                  <Breadcumbs screen={ScreenConstants.BENEFIT_DETAILS} />
                )}
                <Heading serif data-cy="main-heading">
                  Find benefits for {memberInContext?.first_name || ''}
                </Heading>
                <div className="benefitsrchflexdisplay">
                  Type in your search here or browse benefits categories below
                  <Popover
                    content={
                      <div>
                        <strong>Benefits search tips</strong>
                        <br />
                        <br />
                        If you are not seeing results or not seeing the result
                        you expect, try modifying your search.
                        <br />
                        <strong>Prescription</strong> instead of Medication
                        <br />
                        <strong>Transportation</strong> instead of Transit
                        <br />
                        <strong>OTC</strong> (over-the-counter) to search for
                        Pharmacy benefits
                        <br />
                        <br />
                        If your search did not find benefits for addressing an
                        identified need, you may want to search by Value-added
                        services.
                        <br />
                        <strong>Value-added services</strong> can include a wide
                        variety of benefits such as meal service, fitness, home
                        and bath safety devices, diapers and more.
                      </div>
                    }
                    className="benefitsrchspacing"
                  >
                    <IconSymbol icon="question_exchange" />
                  </Popover>
                </div>
                <Grid>
                  <Grid.Col span={{ xs: 12, md: 12, lg: 6 }}>
                    <FormProvider state={form} onSubmit={onSubmit}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-end',
                          gap: '1rem',
                        }}
                      >
                        <TextInput
                          placeholder="Select a Benefit"
                          key="benefitSearch-1"
                          model="textForm"
                          isClearable
                        />
                        <div style={{ flexShrink: 0 }}>
                          <Button
                            type="submit"
                            size="$md"
                            id="details_search"
                          >
                            Search
                          </Button>
                        </div>
                      </div>
                    </FormProvider>
                  </Grid.Col>
                </Grid>
              </div>
            </Grid.Col>
            <Grid.Col span={{ xs: 12, md: 4, lg: 4 }}>
              <MemberInformation
                name={`${memberInContext?.first_name} ${memberInContext?.last_name}`}
                memberId={memberInContext?.member_id || ''}
                dob={memberInContext?.DOB || ''}
                zipcode={memberInContext?.zipCode || ''}
                vendor={vendor}
                lob={memberInContext?.lineOfBusiness || ''}
                showAssessmentButton={
                  isUniteUsApiEnabled
                    ? !memberInContext?.medSup &&
                      sessionStorage.getItem('UHC-CC-Screening') === 'false'
                    : vendor !== 'Unite Us' &&
                      !memberInContext?.medSup &&
                      sessionStorage.getItem('UHC-CC-Screening') === 'false'
                }
              />
            </Grid.Col>
          </Grid>
        </div>
      </div>
      <div>
        {' '}
        <div
          style={{
            paddingRight: '5rem',
            paddingLeft: '5rem',
            paddingTop: '3rem',
            paddingBottom: '2rem',
          }}
        >
          <IconSymbol
            id="arrow_back_ios"
            icon="arrow_back_ios"
            size="xs"
            onClick={() => {
              navigate(`/planbenefits`);
            }}
          />
          <span>
            <Text
              data-cy="back-to-plan-benefits"
              style={{
                cursor: 'pointer',
              }}
              color="$core.color.brand.60"
              fontWeight="medium"
              fontSize="lg"
              onClick={() => {
                navigate(`/planbenefits`);
              }}
            >
              Back to Plan Benefits
            </Text>
          </span>
          <BenefitDetailsResults Id={benefitId}/>
        </div>
      </div>
    </React.Fragment>
  );
};
